import { RefObject, useImperativeHandle, useRef, useState } from 'react';

type Session<T> = {
  input: T | undefined;
} & {
  onCancel: (() => void) | undefined;
  onSubmit: ((value: T) => void) | undefined;
};

export type RenderModal<TData, TProps> = React.FC<
  TProps & {
    isOpen: boolean;
  } & Partial<Session<TData>>
>;

export function PromiseDialogFactory<TData, TProps>(
  RenderModal: RenderModal<TData, TProps>,
) {
  type PromiseModalRef = {
    open: (input: TData) => Promise<undefined | TData>;
  };

  const usePromiseModal = () => {
    const modalRef = useRef<PromiseModalRef>(null);
    const open = (input: TData) => modalRef?.current?.open(input);
    return { props: { modalRef }, open };
  };

  const PromiseModal = ({
    modalRef,
    ...rest
  }: {
    modalRef: RefObject<PromiseModalRef | null>;
  } & TProps) => {
    // use assertion to prevent TS typing derivation issue.
    const props = rest as TProps;

    const [session, setSession] = useState<Session<TData>>();
    const isOpen = !!session;

    useImperativeHandle(modalRef, () => ({
      open: async (input) => {
        const promise = new Promise<TData | undefined>((resolve) => {
          setSession({
            input,
            onCancel() {
              setSession(undefined);
              resolve(undefined);
            },
            onSubmit(value: TData) {
              setSession(undefined);
              resolve(value);
            },
          });
        });
        const output = await promise;
        setSession(undefined);

        return output;
      },
    }));

    return (
      <RenderModal
        isOpen={isOpen}
        onCancel={session?.onCancel}
        onSubmit={session?.onSubmit}
        input={session?.input}
        {...props}
      />
    );
  };

  return {
    useModal: usePromiseModal,
    Modal: PromiseModal,
  };
}
